import { fetchWallets } from '../../api/wallets.js';
import { fetchTransactions } from '../../api/transactions.js';
import { fetchGlobalBudget } from '../../api/budgets.js';
import { fetchCategories } from '../../api/categories.js';
import { appState } from '../../store/appState.js';

export async function renderDashboardTab() {
    const dashSummary = document.getElementById('dash-summary');
    
    if (!dashSummary) return;

    dashSummary.innerHTML = '<p class="text-center text-gray-500"><i class="fa-solid fa-spinner fa-spin"></i> Loading dashboard...</p>';

    const wallets = await fetchWallets();
    // Dùng cache nếu có (do refreshAllActiveViews đã pre-fetch)
    if (!appState.currentTransactionsList || appState.currentTransactionsList.length === 0) {
        appState.currentTransactionsList = await fetchTransactions();
    }
    const txns = appState.currentTransactionsList;

    // 1. TÍNH TỔNG SỐ DƯ THEO TỪNG LOẠI TIỀN TỆ (Dựa trên ví đang active)
    const balances = {};
    wallets.forEach(w => {
        if (!balances[w.currency]) balances[w.currency] = 0;
        balances[w.currency] += Number(w.balance);
    });

    // 2. TÍNH TÓAN THỐNG KÊ (THÁNG NÀY, THÁNG TRƯỚC VÀ TRUNG BÌNH) THEO TIỀN TỆ
    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${now.getMonth()}`;
    
    let lastMonth = now.getMonth() - 1;
    let lastMonthYear = now.getFullYear();
    if (lastMonth < 0) {
        lastMonth = 11;
        lastMonthYear--;
    }
    const lastMonthKey = `${lastMonthYear}-${lastMonth}`;

    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const past12MonthsStart = new Date(now.getFullYear(), now.getMonth() - 12, 1);

    const stats = {}; // { 'VND': { thisIn: 0, thisOut: 0, lastIn: 0, lastOut: 0, past12In: 0, past12Out: 0 } }

    txns.forEach(t => {
        const cur = t.currency || 'VND';
        if (!stats[cur]) {
            stats[cur] = { thisIn: 0, thisOut: 0, lastIn: 0, lastOut: 0, past12In: 0, past12Out: 0 };
        }

        const d = new Date(t.transaction_date);
        const mKey = `${d.getFullYear()}-${d.getMonth()}`;
        const amount = Number(t.amount); // Không đổi tỷ giá, giữ nguyên bản địa

        if (t.linked_txn_id) return; // Bỏ qua giao dịch chuyển tiền cho các thống kê UI

        if (mKey === currentMonthKey) {
            if (t.type === 'income') stats[cur].thisIn += amount;
            else if(t.type === 'expense') stats[cur].thisOut += amount;
        } else {
            if (mKey === lastMonthKey) {
                if (t.type === 'income') stats[cur].lastIn += amount;
                else if(t.type === 'expense') stats[cur].lastOut += amount;
            }
            // Add to past 12 months accumulator (excluding current month)
            if (d >= past12MonthsStart && d < currentMonthStart) {
                if (t.type === 'income') stats[cur].past12In += amount;
                else if(t.type === 'expense') stats[cur].past12Out += amount;
            }
        }
    });

    // 3. RENDER UI TỔNG HỢP DASHBOARD
    let html = '';

    // A. Khối Total Balance
    html += `<div class="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-5 mb-5 shadow-lg border border-gray-700">`;
    html += `<h3 class="text-sm text-gray-400 mb-4 uppercase tracking-wider font-bold">Total Balances</h3>`;
    
    if (Object.keys(balances).length === 0) {
        html += `<div class="text-white text-xl font-bold">0 VND</div>`;
    } else {
        for (const [currency, amt] of Object.entries(balances)) {
            html += `<div class="mb-4 last:mb-0 border-b border-gray-700/50 pb-4 last:border-0 last:pb-0">`;
            html += `<div class="text-3xl font-bold text-white mb-2">${amt.toLocaleString('en-US')} <span class="text-sm font-normal text-gray-400">${currency}</span></div>`;
            
            const data = stats[currency] || { thisIn: 0, thisOut: 0, past12In: 0, past12Out: 0 };
            const total12mIn = data.past12In + data.thisIn;
            const total12mOut = data.past12Out + data.thisOut;

            html += `<div class="flex items-center text-xs gap-4 bg-darkbg/50 p-2 rounded-lg inline-flex border border-gray-800/50">
                <span class="text-gray-500 font-medium">Past 12m:</span>
                <span class="text-income font-bold flex items-center"><i class="fa-solid fa-arrow-down mr-1 text-[10px]"></i>+${total12mIn.toLocaleString('en-US')}</span>
                <span class="text-expense font-bold flex items-center"><i class="fa-solid fa-arrow-up mr-1 text-[10px]"></i>-${total12mOut.toLocaleString('en-US')}</span>
            </div>`;
            html += `</div>`;
        }
    }
    html += `</div>`;

    // A2. Global Expense Limit Section (Hiển thị 1 thanh duy nhất, không cộng dồn)
    try {
        const globalBudget = await fetchGlobalBudget();
        if (globalBudget) {
            const limitAmount = Number(globalBudget.monthly_amount);
            
            // Tính tổng chi tiêu trong tháng hiện tại (không tính tiền transfer)
            const spentThisMonth = txns
                .filter(t => {
                    if (t.type !== 'expense' || t.linked_txn_id) return false;
                    const d = new Date(t.transaction_date);
                    return `${d.getFullYear()}-${d.getMonth()}` === currentMonthKey;
                })
                .reduce((sum, t) => sum + Number(t.base_amount || t.amount), 0);

            const available = limitAmount - spentThisMonth;
            const pct = Math.min(100, Math.round((spentThisMonth / limitAmount) * 100));
            const isOver = available < 0;

            // Xử lý màu sắc: Vượt hạn mức thì báo đỏ
            const barColor = isOver ? 'bg-expense' : 'bg-primary'; 
            const availText = isOver
                ? `<span class="text-expense font-bold">-${Math.abs(available).toLocaleString('en-US')} over</span>`
                : `<span class="text-primary font-bold">+${available.toLocaleString('en-US')} left</span>`;

            html += `<div class="bg-cardbg rounded-2xl p-4 mb-5 border border-gray-700 shadow-sm">`;
            html += `<div class="flex justify-between items-center mb-3">
                <h3 class="text-base font-bold text-gray-200"><i class="fa-solid fa-bullseye mr-2 text-primary"></i>Monthly Limit</h3>
                ${availText}
            </div>`;
            html += `<div class="w-full bg-gray-800 h-3 rounded-full overflow-hidden mb-2 shadow-inner">
                <div class="h-full ${barColor} transition-all duration-500 ease-out" style="width:${pct}%"></div>
            </div>`;
            html += `<div class="flex justify-between text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                <span>${spentThisMonth.toLocaleString('en-US')} spent</span>
                <span>${limitAmount.toLocaleString('en-US')} limit</span>
            </div>`;
            html += `</div>`;
        }
    } catch(e) { console.error('Lỗi render Global Limit:', e); }

    // B. Khối Thống kê dòng tiền (CashFlow)
    // Lọc chỉ các loại tiền tệ của các VÍ ĐANG HOẠT ĐỘNG
    const activeCurrencies = Object.keys(balances);

    for (const currency of activeCurrencies) {
        const data = stats[currency] || { thisIn: 0, thisOut: 0, lastIn: 0, lastOut: 0, past12In: 0, past12Out: 0 };
        const netThisMonth = data.thisIn - data.thisOut;
        
        // Tính Diffs (Lượng tiền chênh lệch)
        const diffInLastMo = data.thisIn - data.lastIn;
        const diffOutLastMo = data.thisOut - data.lastOut;

        // Tính % MoM
        let inPercent = 0, outPercent = 0;
        if (data.lastIn > 0) inPercent = (diffInLastMo / data.lastIn) * 100;
        if (data.lastOut > 0) outPercent = (diffOutLastMo / data.lastOut) * 100;

        const avg12In = data.past12In / 12;
        const avg12Out = data.past12Out / 12;

        const diffInAvg = data.thisIn - avg12In;
        const diffOutAvg = data.thisOut - avg12Out;

        let inAvgPercent = 0, outAvgPercent = 0;
        if (avg12In > 0) inAvgPercent = (diffInAvg / avg12In) * 100;
        if (avg12Out > 0) outAvgPercent = (diffOutAvg / avg12Out) * 100;

        // Xử lý Màu sắc (Logic: Thu nhập tăng = Xanh, giảm = Đỏ. Chi tiêu tăng = Đỏ, giảm = Xanh)
        const inColor = diffInLastMo >= 0 ? 'text-income' : 'text-expense';
        const inAvgColor = diffInAvg >= 0 ? 'text-income' : 'text-expense';
        const outColor = diffOutLastMo > 0 ? 'text-expense' : 'text-income'; // Chi tiêu TĂNG (diff > 0) là XẤU (Đỏ)
        const outAvgColor = diffOutAvg > 0 ? 'text-expense' : 'text-income';

        // Chuỗi văn bản %
        const inPStr = inPercent > 0 ? `+${inPercent.toFixed(1)}%` : `${inPercent.toFixed(1)}%`;
        const outPStr = outPercent > 0 ? `+${outPercent.toFixed(1)}%` : `${outPercent.toFixed(1)}%`;
        const inAvgPStr = inAvgPercent > 0 ? `+${inAvgPercent.toFixed(1)}%` : `${inAvgPercent.toFixed(1)}%`;
        const outAvgPStr = outAvgPercent > 0 ? `+${outAvgPercent.toFixed(1)}%` : `${outAvgPercent.toFixed(1)}%`;

        // Chuỗi văn bản Tiền chênh lệch
        const diffInLastMoStr = diffInLastMo > 0 ? `+${diffInLastMo.toLocaleString('en-US')}` : `${diffInLastMo.toLocaleString('en-US')}`;
        const diffOutLastMoStr = diffOutLastMo > 0 ? `+${diffOutLastMo.toLocaleString('en-US')}` : `${diffOutLastMo.toLocaleString('en-US')}`;
        const diffInAvgStr = diffInAvg > 0 ? `+${diffInAvg.toLocaleString('en-US', {maximumFractionDigits: 0})}` : `${diffInAvg.toLocaleString('en-US', {maximumFractionDigits: 0})}`;
        const diffOutAvgStr = diffOutAvg > 0 ? `+${diffOutAvg.toLocaleString('en-US', {maximumFractionDigits: 0})}` : `${diffOutAvg.toLocaleString('en-US', {maximumFractionDigits: 0})}`;

        html += `
            <div class="bg-cardbg rounded-2xl p-4 mb-4 border border-gray-700 shadow-sm">
                <div class="flex justify-between items-center mb-4 border-b border-gray-800 pb-3">
                    <h3 class="text-base font-bold text-gray-200">Cash Flow (${currency})</h3>
                    <span class="text-xs bg-gray-800 text-gray-400 px-2 py-1 rounded border border-gray-700">This Month</span>
                </div>
                
                <div class="flex flex-col gap-4">
                    <!-- INCOME ROW -->
                    <div class="bg-darkbg p-4 rounded-xl border border-gray-800">
                        <div class="text-xs text-gray-500 mb-1 uppercase font-bold tracking-wider">Income</div>
                        <div class="font-bold text-income text-2xl mb-3">+${data.thisIn.toLocaleString('en-US')} <span class="text-sm font-normal text-gray-400">${currency}</span></div>
                        <div class="flex flex-col gap-2">
                            ${data.lastIn > 0 ? `<div class="text-xs ${inColor} font-medium flex justify-between"><span>vs Last Mo (${data.lastIn.toLocaleString('en-US')})</span> <span>${diffInLastMoStr} (${inPStr})</span></div>` : '<div class="text-xs text-gray-600">No data last mo</div>'}
                            ${avg12In > 0 ? `<div class="text-xs ${inAvgColor} font-medium flex justify-between"><span>vs 12m Avg (${avg12In.toLocaleString('en-US', {maximumFractionDigits: 0})})</span> <span>${diffInAvgStr} (${inAvgPStr})</span></div>` : '<div class="text-xs text-gray-600">No 12m data</div>'}
                        </div>
                    </div>
                    
                    <!-- EXPENSE ROW -->
                    <div class="bg-darkbg p-4 rounded-xl border border-gray-800">
                        <div class="text-xs text-gray-500 mb-1 uppercase font-bold tracking-wider">Expense</div>
                        <div class="font-bold text-expense text-2xl mb-3">-${data.thisOut.toLocaleString('en-US')} <span class="text-sm font-normal text-gray-400">${currency}</span></div>
                        <div class="flex flex-col gap-2">
                            ${data.lastOut > 0 ? `<div class="text-xs ${outColor} font-medium flex justify-between"><span>vs Last Mo (${data.lastOut.toLocaleString('en-US')})</span> <span>${diffOutLastMoStr} (${outPStr})</span></div>` : '<div class="text-xs text-gray-600">No data last mo</div>'}
                            ${avg12Out > 0 ? `<div class="text-xs ${outAvgColor} font-medium flex justify-between"><span>vs 12m Avg (${avg12Out.toLocaleString('en-US', {maximumFractionDigits: 0})})</span> <span>${diffOutAvgStr} (${outAvgPStr})</span></div>` : '<div class="text-xs text-gray-600">No 12m data</div>'}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    dashSummary.innerHTML = html;
}
